import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AmorgAdminToken, AmorgAdminUserListing, OrganizationNote, OrganizationNoteFlag, OrganizationNoteFlags } from '../../../amorg-common';
import { createOrganizationNote, deleteOrganizationNote, getActiveUser, getOrganizationNotes, getUsers, updateOrganizationNote } from '../../../api/adminClientApi';
import { AdminSiteTabs } from '../_components/AdminSiteTabs';
import { AmorgAdminPageProvider } from '../AmorgAdminPageContext';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { MessageCard } from '../../../designSystem/Message';
import { PageSection } from '../../../designSystem/PageSection';
import { Card } from '../../../designSystem/Card';
import { Select } from '../../../designSystem/inputs/Select';
import { TextAreaWithSave } from '../../../designSystem/inputs/TextAreaWithSave';
import { DeleteConfirmationButton } from '../../../designSystem/compositeComponents/DeleteConfirmationButton';
import { BaseMessagePanel } from '../../../designSystem/compositeComponents/BaseMessagePanel';
import { SafeFlagSelector } from '../../../designSystem/compositeComponents/SafeFlagSelector';

export function AmorgAdminOrganizationNotes (): JSX.Element
{
    const [searchParams] = useSearchParams();
    const { organizationId } = useParams<{ organizationId: string }>();
    const siteId = searchParams.get('siteId') || undefined;
    const navigate = useNavigate();
    const [notes, setNotes] = React.useState<OrganizationNote[]>([]);
    const [users, setUsers] = React.useState<AmorgAdminUserListing[]>([]);
    const [flagFilter, setFlagFilter] = React.useState<OrganizationNoteFlag[]>([]);
    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');

    async function loadNotes ()
    {
        if(!organizationId)
        {
            setError('Mangler organizationId i URL');
            setLoading(false);
            return;
        }

        try
        {
            setNotes(await getOrganizationNotes(organizationId, () => navigate('/amorgy/login')));
            setActiveUser(await getActiveUser(() => navigate('/amorgy/login')));
            setError('');
        }
        catch
        {
            setError('Noter kunne ikke indlæses');
        }
        finally
        {
            setLoading(false);
        }
    }

    React.useEffect(() =>
    {
        async function getData ()
        {
            const users = await getUsers(() => navigate('/amorgy/login'));
            setUsers(users || []);
        }
        getData();
    }, [organizationId]);

    React.useEffect(() =>
    {
        loadNotes();
    }, [organizationId]);

    async function handleCreateNote (content:string, tags?:string[])
    {
        if(!organizationId)
            return { errorMessage: 'Noten kunne ikke oprettes' };

        const created = await createOrganizationNote(
            organizationId,
            {
                flags: tags === undefined ? [] : tags as OrganizationNoteFlag[],
                note: content,
            },
            () => navigate('/amorgy/login')
        );
        if(!created)
        {
            setError('Noten kunne ikke oprettes');
            return { errorMessage: 'Noten kunne ikke oprettes' };
        }
        await loadNotes();
        return { errorMessage: undefined };
    }

    async function handleUpdateNote (id:string, content:string, tags?:string[])
    {
        if(!organizationId)
            return { errorMessage: 'Noten kunne ikke gemmes' };

        const noteObject = notes.find(n => n.id === id);
        if(!noteObject)
            return { errorMessage: 'Noten kunne ikke gemmes' };

        const updated = await updateOrganizationNote(
            organizationId,
            { ...noteObject, flags: tags === undefined ? noteObject.flags : tags as OrganizationNoteFlag[], note:content, updated: new Date() },
            () => navigate('/amorgy/login')
        );
        if(!updated)
        {
            setError('Noten kunne ikke gemmes');
            return { errorMessage: 'Noten kunne ikke gemmes' };
        }
        await loadNotes();
        return { errorMessage: undefined };
    }

    async function handleDeleteNote (noteId: string)
    {
        if(!organizationId)
            return { errorMessage: 'Noten kunne ikke slettes' };

        const deleted = await deleteOrganizationNote(organizationId, noteId, () => navigate('/amorgy/login'));
        if(!deleted)
        {
            setError('Noten kunne ikke slettes');
            return { errorMessage: 'Noten kunne ikke slettes' };
        }
        await loadNotes();
        return { errorMessage: undefined };
    }

    const filteredNotes = React.useMemo(() => {
        if(flagFilter.length === 0)
            return notes;
        else
            return notes.filter(note => note.flags.some(flag => flagFilter.includes(flag)));
    }, [notes, flagFilter]);

    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout
                noDivider
                headerContainer={
                    <Flex flexDirection="column" gap={16}>
                        <AdminSiteTabs selectedAdminTab="organizations" selectedSiteTab="notes" orgId={organizationId} siteId={siteId} />
                    </Flex>
                }
                contentContainer={
                    <Flex flexDirection="column" gap={24}>
                        {error && <MessageCard variant="error">{error}</MessageCard>}
                        {/* <Flex justifyContent="flex-end">
                            <Button variant="text" text="Tilføj note" icon="actions_plus" onClick={handleCreateNote} />
                        </Flex> */}

                        <SafeFlagSelector
                            colorMode='colorIndex'
                            allowedFlags={OrganizationNoteFlags.map(flag => flag)}
                            selectedFlags={flagFilter}
                            onChange={(newFlags) => setFlagFilter(newFlags as OrganizationNoteFlag[])} />

                        <PageSection header={{ title: 'Noter', defaultCollapsed: false }}>

                            <BaseMessagePanel
                                texts={{
                                    addButton: 'Tilføj note',
                                    deletedConfirmation: 'Noten er slettet',
                                    deleteModalConfirmation: 'Er du sikker på, at du vil slette noten?',
                                    deleteModalHeading: 'Slet note',
                                    editModalHeader: 'Rediger note',
                                    editModalLabel: 'Rediger note',
                                    noComments: 'Ingen noter oprettet',
                                }}
                                messages={filteredNotes.map(note => {
                                    const user = users.find(u => u.userId === note.createdByUserId);
                                    const createdBy = user
                                        ? { userId: user.userId, displayName: user.email }
                                        : { userId: note.createdByUserId, displayName: 'Ukendt bruger' };

                                    const canChange = activeUser && (activeUser.superAdmin || activeUser.userId === note.createdByUserId) && !note.deleted;

                                    const onEdit = canChange
                                        ? (_id: string, content: string, tags?: string[]) => handleUpdateNote(note.id, content, tags)
                                        : undefined;

                                    const onDelete = canChange
                                        ? () => handleDeleteNote(note.id)
                                        : undefined;

                                    return {
                                        id: note.id,
                                        content: note.deleted ? '[Slettet]' : note.note,
                                        tags: note.flags,
                                        createdBy,
                                        created: note.created,
                                        updated: note.updated,
                                        deleted: note.deleted,
                                        onEdit,
                                        onDelete,
                                    }
                                })}
                                availableTags={OrganizationNoteFlags.map(flag => flag)}
                                maxHeightPx={99999999999}
                                createDisabled={false}
                                messageValidator={content => {
                                    if(content.length < 1)
                                        return { errorMessage: 'Noten må ikke være tom' };

                                    return { errorMessage: undefined };
                                }}
                                onCreate={handleCreateNote}
                            />

                            {/* {notes.length === 0 && <Card variant="none">Ingen noter</Card>}
                            {notes.map(note =>
                                <NoteCard
                                    key={note.id}
                                    note={note}
                                    activeUser={activeUser}
                                    onUpdate={changes => handleUpdateNote(note, changes)}
                                    onDelete={() => handleDeleteNote(note.id)} />
                            )} */}

                        </PageSection>
                    </Flex>
                } />
        </AmorgAdminPageProvider>
    );
}

function NoteCard (props: {
    note: OrganizationNote,
    activeUser: AmorgAdminToken | null,
    onUpdate: (changes: Partial<OrganizationNote>) => void,
    onDelete: () => void
}): JSX.Element
{
    const canEdit = !props.note.deleted && !!props.activeUser && (props.activeUser.superAdmin || props.activeUser.userId === props.note.createdByUserId);
    const canDelete = !!props.activeUser?.superAdmin && !props.note.deleted;

    return (
        <Card>
            <Flex flexDirection="column" gap={12}>
                <Flex gap={12} alignItems="flex-end">
                    <Select
                        selectedValue={props.note.flags[0] || 'generel note'}
                        disabled={!canEdit}
                        onChange={value => props.onUpdate({ flags: [value as typeof OrganizationNoteFlags[number]] })}
                        options={OrganizationNoteFlags.map(flag => ({ value: flag, text: flag }))}
                        width="180px" />
                    <span>Oprettet {props.note.created.toLocaleDateString()}</span>
                    {props.note.updated && <span>Opdateret {props.note.updated.toLocaleDateString()}</span>}
                    {canDelete &&
                        <DeleteConfirmationButton
                            asIcon
                            buttonText="Slet"
                            confirmationHeading="Slet note"
                            confirmationText="Noten slettes ved at tømme teksten og markere den som slettet."
                            requireKey
                            onConfirmDelete={props.onDelete} />
                    }
                </Flex>
                <TextAreaWithSave
                    value={props.note.deleted ? '[Slettet]' : props.note.note}
                    disabled={!canEdit}
                    onSave={value => props.onUpdate({ note: value })}
                    minRows={4} />
            </Flex>
        </Card>
    );
}
